import React, { useCallback, useEffect, useMemo, useState } from "react";
import { auth, db } from "./firebase";
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { openBrandedPrescription } from "./prescriptionUtils";
import { sendUserNotification } from "./notificationApi";
import {
  canJoinConsultationCall,
  getConsultationJoinLabel,
  isVetConsultationBooking,
  openConsultationCallWindow,
} from "./consultationUtils";
import { EmptyState, PageHeader, StatCard, ToneBadge } from "./petaarUi";

const REASSIGN_BOOKING_URL =
  process.env.REACT_APP_REASSIGN_BOOKING_URL ||
  "https://us-central1-petaar-3dcab.cloudfunctions.net/reassignBooking";
const VET_OPS_URL =
  process.env.REACT_APP_VET_OPS_URL ||
  "https://us-central1-petaar-3dcab.cloudfunctions.net/vetOps";

const getAmount = (booking) => Number(booking?.amount) || 0;
const getCommission = (booking) => Number(booking?.commissionAmount) || getAmount(booking) * 0.2;
const getNet = (booking) => Number(booking?.providerEarning) || getAmount(booking) * 0.8;
const getBookingTs = (booking) =>
  booking?.createdAt?.seconds ? booking.createdAt.seconds * 1000 :
    booking?.assignedAt?.seconds ? booking.assignedAt.seconds * 1000 : 0;
const maskEmail = (email) => {
  const text = String(email || "").trim();
  if (!text.includes("@")) return "Customer";
  const [local, domain] = text.split("@");
  if (!local || !domain) return "Customer";
  const visible = local.slice(0, 2);
  const hidden = "*".repeat(Math.max(2, local.length - 2));
  return `${visible}${hidden}@${domain}`;
};
const ownerLabel = (booking) => maskEmail(booking?.ownerEmail || booking?.userEmail || "");
const formatTs = (ts) => (ts?.seconds ? new Date(ts.seconds * 1000).toLocaleString() : "N/A");
const consultationStatusLabel = (booking) => {
  const raw = String(booking?.consultationStatus || "").toLowerCase();
  if (!raw) return "pending";
  const map = {
    assignment_in_progress: "Finding vet",
    waiting_vet: "Waiting for available vet",
    vet_assigned: "Vet assigned",
    vet_accepted: "Vet accepted",
    consultation_live: "Consultation live",
    prescription_sent: "Prescription sent",
    consultation_completed: "Consultation completed",
    consultation_cancelled: "Consultation cancelled",
  };
  return map[raw] || raw.replaceAll("_", " ");
};

const bookingTone = (status) => {
  const value = String(status || "").toLowerCase();
  if (value === "completed") return "success";
  if (value === "accepted" || value === "in_progress") return "accent";
  if (value === "cancelled" || value === "rejected") return "danger";
  if (value === "cancel_requested") return "warning";
  return "default";
};

function toCsv(rows) {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const escape = (value) => {
    const text = String(value ?? "");
    if (text.includes(",") || text.includes('"') || text.includes("\n")) {
      return `"${text.replaceAll('"', '""')}"`;
    }
    return text;
  };
  const lines = [headers.join(",")];
  rows.forEach((row) => lines.push(headers.map((h) => escape(row[h])).join(",")));
  return lines.join("\n");
}

export default function ProviderBookings({ user }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [updatingId, setUpdatingId] = useState("");
  const [nowTick, setNowTick] = useState(Date.now());
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [search, setSearch] = useState("");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [prescriptionsByBooking, setPrescriptionsByBooking] = useState({});
  const [prescriptionDrafts, setPrescriptionDrafts] = useState({});
  const [sendingPrescriptionFor, setSendingPrescriptionFor] = useState("");
  const [chatUnreadByBooking, setChatUnreadByBooking] = useState({});

  const fetchBookings = useCallback(async () => {
    if (!user?.uid) {
      setBookings([]);
      return;
    }

    setLoading(true);
    try {
      setLoadError("");
      const q = query(collection(db, "bookings"), where("providerId", "==", user.uid));
      const emailQ = user?.email
        ? query(collection(db, "bookings"), where("providerEmail", "==", user.email))
        : null;
      const [snapshot, emailSnapshot] = await Promise.all([
        getDocs(q),
        emailQ ? getDocs(emailQ) : Promise.resolve({ docs: [] }),
      ]);
      const merged = [...snapshot.docs, ...emailSnapshot.docs];
      const byId = new Map(merged.map((d) => [d.id, { id: d.id, ...d.data() }]));
      setBookings([...byId.values()]);
    } catch (error) {
      setLoadError(error.message || "Unable to load bookings.");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, user?.email]);

  useEffect(() => {
    if (!user?.uid) {
      setBookings([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setLoadError("");

    const providerRows = new Map();
    const legacyRows = new Map();
    let providerReady = false;
    let legacyReady = !user?.email;

    const applyRows = () => {
      const merged = new Map([...providerRows, ...legacyRows]);
      setBookings([...merged.values()]);
    };
    const finishLoading = () => {
      if (providerReady && legacyReady) {
        setLoading(false);
      }
    };

    const providerQuery = query(collection(db, "bookings"), where("providerId", "==", user.uid));
    const unsubscribeProvider = onSnapshot(
      providerQuery,
      (snapshot) => {
        providerReady = true;
        providerRows.clear();
        snapshot.docs.forEach((item) => {
          providerRows.set(item.id, { id: item.id, ...item.data() });
        });
        setLoadError("");
        applyRows();
        finishLoading();
      },
      (error) => {
        providerReady = true;
        setLoadError(error.message || "Unable to load bookings.");
        finishLoading();
      },
    );

    let unsubscribeLegacy = () => {};
    if (user?.email) {
      const legacyQuery = query(collection(db, "bookings"), where("providerEmail", "==", user.email));
      unsubscribeLegacy = onSnapshot(
        legacyQuery,
        (snapshot) => {
          legacyReady = true;
          legacyRows.clear();
          snapshot.docs.forEach((item) => {
            legacyRows.set(item.id, { id: item.id, ...item.data() });
          });
          setLoadError("");
          applyRows();
          finishLoading();
        },
        (error) => {
          legacyReady = true;
          setLoadError(error.message || "Unable to load legacy provider bookings.");
          finishLoading();
        },
      );
    }

    return () => {
      unsubscribeProvider();
      unsubscribeLegacy();
    };
  }, [user?.uid, user?.email]);

  useEffect(() => {
    if (!user?.uid) {
      setPrescriptionsByBooking({});
      return undefined;
    }

    const byProviderId = new Map();
    const byProviderEmail = new Map();
    let idReady = false;
    let emailReady = !user?.email;

    const applyRows = () => {
      const merged = new Map([...byProviderId, ...byProviderEmail]);
      const next = {};
      merged.forEach((value, key) => {
        next[key] = value;
      });
      setPrescriptionsByBooking(next);
    };

    const idQuery = query(collection(db, "prescriptions"), where("providerId", "==", user.uid));
    const unsubscribeId = onSnapshot(
      idQuery,
      (snapshot) => {
        idReady = true;
        byProviderId.clear();
        snapshot.docs.forEach((item) => {
          const data = item.data() || {};
          const bookingId = String(data.bookingId || "").trim();
          if (!bookingId) return;
          byProviderId.set(bookingId, { id: item.id, ...data });
        });
        applyRows();
      },
      () => {
        idReady = true;
        applyRows();
      },
    );

    let unsubscribeEmail = () => {};
    if (user?.email) {
      const emailQuery = query(collection(db, "prescriptions"), where("providerEmail", "==", user.email));
      unsubscribeEmail = onSnapshot(
        emailQuery,
        (snapshot) => {
          emailReady = true;
          byProviderEmail.clear();
          snapshot.docs.forEach((item) => {
            const data = item.data() || {};
            const bookingId = String(data.bookingId || "").trim();
            if (!bookingId) return;
            byProviderEmail.set(bookingId, { id: item.id, ...data });
          });
          applyRows();
        },
        () => {
          emailReady = true;
          applyRows();
        },
      );
    }

    if (idReady && emailReady) applyRows();
    return () => {
      unsubscribeId();
      unsubscribeEmail();
    };
  }, [user?.uid, user?.email]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // CONSULTATION CHAT: Track unread owner messages for vet chat badges.
  useEffect(() => {
    if (!user?.uid) {
      setChatUnreadByBooking({});
      return undefined;
    }
    let cancelled = false;
    const unsubs = [];
    const attachListeners = async () => {
      try {
        const chatsSnap = await getDocs(query(
          collection(db, "consultationChats"),
          where("providerUid", "==", user.uid),
        ));
        if (cancelled) return;
        const chatBookingIds = new Set(chatsSnap.docs.map((docSnap) => docSnap.id));
        const activeVetBookings = bookings.filter((booking) =>
          isVetConsultationBooking(booking) &&
          ["accepted", "in_progress"].includes(String(booking.status || "").toLowerCase()) &&
          chatBookingIds.has(String(booking.id || "").trim()),
        );
        if (!activeVetBookings.length) {
          setChatUnreadByBooking({});
          return;
        }
        activeVetBookings.forEach((booking) => {
          const bookingId = String(booking.id || "").trim();
          const unreadQuery = query(
            collection(db, "consultationChats", bookingId, "messages"),
            where("senderRole", "==", "owner"),
          );
          const unsub = onSnapshot(
            unreadQuery,
            (snapshot) => {
              const unreadCount = snapshot.docs.filter((docSnap) => docSnap.data()?.read !== true).length;
              setChatUnreadByBooking((prev) => ({
                ...prev,
                [bookingId]: unreadCount,
              }));
            },
            () => {
              setChatUnreadByBooking((prev) => ({
                ...prev,
                [bookingId]: 0,
              }));
            },
          );
          unsubs.push(unsub);
        });
      } catch (_) {
        if (!cancelled) {
          setChatUnreadByBooking({});
        }
      }
    };
    void attachListeners();

    return () => {
      cancelled = true;
      unsubs.forEach((unsub) => unsub());
    };
  }, [bookings, user?.uid]);

  const getSlaText = (booking) => {
    const status = String(booking.status || "").toLowerCase();
    if (status !== "pending") {
      return "";
    }
    const assignedMs =
      booking.assignedAt?.seconds ? booking.assignedAt.seconds * 1000 :
        booking.createdAt?.seconds ? booking.createdAt.seconds * 1000 : 0;
    if (!assignedMs) return "SLA timer unavailable";

    const deadlineMs = assignedMs + 20 * 60 * 1000;
    const remainingMs = deadlineMs - nowTick;
    if (remainingMs <= 0) return "SLA breached - respond immediately";
    const mins = Math.ceil(remainingMs / 60000);
    return `Respond within ${mins} min`;
  };

  const updateStatus = async (bookingId, status) => {
    try {
      setUpdatingId(bookingId);
      const target = bookings.find((b) => b.id === bookingId);
      if (!target) {
        throw new Error("Booking not found.");
      }

      if (status === "rejected") {
        const token = await auth.currentUser?.getIdToken();
        if (!token) {
          throw new Error("Session expired. Please login again.");
        }
        const res = await fetch(REASSIGN_BOOKING_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({
            bookingId,
            excludeProviderId: user?.uid || "",
            excludeProviderEmail: user?.email || "",
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data?.error || "Reassignment failed");
        }
        alert("Booking rejected and reassigned.");
        return;
      }

      const updatePayload = { status };
      const isVetBooking = isVetConsultationBooking(target);
      if (status === "accepted") {
        updatePayload.acceptedAt = serverTimestamp();
        if (isVetBooking) {
          updatePayload.consultationStatus = "vet_accepted";
        }
      }
      if (status === "in_progress") {
        updatePayload.inProgressAt = serverTimestamp();
        if (isVetBooking) {
          updatePayload.consultationStatus = "consultation_live";
        }
      }
      if (status === "completed") {
        updatePayload.completedAt = serverTimestamp();
        if (isVetBooking) {
          updatePayload.consultationStatus = "consultation_completed";
        }
      }
      if (status === "cancelled") {
        updatePayload.refundStatus = target.paymentStatus === "paid" ? "refund_requested" : "not_applicable";
        updatePayload.refundRequestedAt = target.paymentStatus === "paid" ? serverTimestamp() : null;
        updatePayload.cancelledAt = serverTimestamp();
        if (isVetBooking) {
          updatePayload.consultationStatus = "consultation_cancelled";
        }
      }
      await updateDoc(doc(db, "bookings", bookingId), updatePayload);

      if (target?.ownerEmail) {
        const baseMessage = `Your booking for ${target.serviceTitle || "service"} is ${status}.`;
        const refundNote = status === "cancelled" && target.paymentStatus === "paid" ?
          " Refund request has been initiated." :
          "";
        await sendUserNotification({
          bookingId,
          recipientEmail: target.ownerEmail,
          type: "booking_status",
          title: `Booking ${status}`,
          message: `${baseMessage}${refundNote}`,
        });
      }

    } catch (err) {
      alert(err.message);
    } finally {
      setUpdatingId("");
    }
  };

  const sendPrescription = async (booking) => {
    const draft = prescriptionDrafts[booking.id] || {};
    const medicines = String(draft.medicines || "").trim();
    if (!medicines) {
      alert("Please write medicines/treatment before sending prescription.");
      return;
    }
    try {
      setSendingPrescriptionFor(booking.id);
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("Session expired. Please login again.");
      const res = await fetch(VET_OPS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: "create_prescription",
          bookingId: booking.id,
          diagnosis: String(draft.diagnosis || "").trim(),
          medicines,
          advice: String(draft.advice || "").trim(),
          followUpDate: String(draft.followUpDate || "").trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || `Prescription send failed (${res.status})`);
      }
      setPrescriptionDrafts((prev) => ({
        ...prev,
        [booking.id]: {
          diagnosis: "",
          medicines: "",
          advice: "",
          followUpDate: "",
        },
      }));
      alert("Prescription sent through Petaar.");
    } catch (error) {
      alert(error.message || "Unable to send prescription.");
    } finally {
      setSendingPrescriptionFor("");
    }
  };

  const filteredBookings = useMemo(() => {
    const now = Date.now();
    const periodDays = periodFilter === "7d" ? 7 : periodFilter === "30d" ? 30 : periodFilter === "90d" ? 90 : 0;
    const fromTs = periodDays ? now - periodDays * 24 * 60 * 60 * 1000 : 0;

    return bookings
      .filter((b) => (statusFilter ? b.status === statusFilter : true))
      .filter((b) => (dateFilter ? b.bookingDate === dateFilter : true))
      .filter((b) => {
        if (!fromTs) return true;
        const ts = getBookingTs(b);
        return ts ? ts >= fromTs : false;
      })
      .filter((b) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (
          String(b.serviceTitle || b.serviceName || "").toLowerCase().includes(q) ||
          ownerLabel(b).toLowerCase().includes(q) ||
          String(b.city || "").toLowerCase().includes(q) ||
          String(b.bookingDate || "").toLowerCase().includes(q)
        );
      });
  }, [bookings, statusFilter, dateFilter, periodFilter, search]);
  const financialSummary = useMemo(() => {
    const paid = filteredBookings.filter((b) => b.paymentStatus === "paid");
    const settled = paid.filter((b) => b.refundStatus !== "refund_processed");
    const grossCollected = paid.reduce((sum, b) => sum + getAmount(b), 0);
    const refundedAmount = paid
      .filter((b) => b.refundStatus === "refund_processed")
      .reduce((sum, b) => sum + getAmount(b), 0);
    const platformFee = settled.reduce((sum, b) => sum + getCommission(b), 0);
    const netPayout = settled.reduce((sum, b) => sum + getNet(b), 0);
    const pendingPayout = filteredBookings
      .filter((b) => b.paymentStatus === "paid" && !["completed", "cancelled", "rejected"].includes(String(b.status || "")))
      .reduce((sum, b) => sum + getNet(b), 0);
    return { grossCollected, refundedAmount, platformFee, netPayout, pendingPayout };
  }, [filteredBookings]);

  const sortedFilteredBookings = useMemo(
    () => [...filteredBookings].sort((a, b) => getBookingTs(b) - getBookingTs(a)),
    [filteredBookings],
  );
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(sortedFilteredBookings.length / Math.max(1, pageSize))),
    [sortedFilteredBookings.length, pageSize],
  );
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, dateFilter, periodFilter, search, pageSize]);
  const pagedBookings = useMemo(() => {
    const safeSize = Math.max(1, pageSize);
    const start = (currentPage - 1) * safeSize;
    return sortedFilteredBookings.slice(start, start + safeSize);
  }, [sortedFilteredBookings, currentPage, pageSize]);
  const exportCsv = () => {
    const rows = filteredBookings.map((b) => ({
      bookingId: b.id,
      serviceTitle: b.serviceTitle || b.serviceName || "",
      customerRef: ownerLabel(b),
      amount: getAmount(b),
      commissionAmount: getCommission(b),
      providerEarning: getNet(b),
      status: b.status || "",
      paymentStatus: b.paymentStatus || "",
      refundStatus: b.refundStatus || "not_requested",
      bookingDate: b.bookingDate || "",
      city: b.city || "",
      category: b.category || "",
    }));
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `provider-bookings-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // CONSULTATION CHAT: Hash navigation entry point from vet bookings list.
  const openConsultationChat = (bookingId) => {
    const safeBookingId = String(bookingId || "").trim();
    if (!safeBookingId) return;
    window.location.hash = `#/consultation/${encodeURIComponent(safeBookingId)}`;
  };

  return (
    <div className="petaar-page">
      <PageHeader
        eyebrow="Provider Operations"
        title="Bookings Queue"
        subtitle="Manage service bookings, instant consultations, prescription handoff, and payout visibility from one operational board."
        actions={(
          <div style={{display: "flex", gap: 10, flexWrap: "wrap"}}>
            <button className="petaar-button petaar-button--secondary" onClick={exportCsv}>
              Export CSV
            </button>
            <button className="petaar-button" onClick={fetchBookings} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        )}
      />

      {loadError ? (
        <div className="petaar-notice error" style={{marginBottom: 16}}>
          {loadError}
        </div>
      ) : null}

      <div className="petaar-stat-grid" style={{marginBottom: 24}}>
        <StatCard label="Total bookings" value={bookings.length} tone="default" meta="All assigned work" />
        <StatCard label="Pending" value={bookings.filter((b) => b.status === "pending").length} tone="warning" meta="Awaiting provider action" />
        <StatCard label="Completed" value={bookings.filter((b) => b.status === "completed").length} tone="success" meta="Finished jobs" />
        <StatCard label="Net payout" value={financialSummary.netPayout} prefix="₹" decimals={2} tone="accent" meta="Settled provider share" />
      </div>

      <div className="petaar-split" style={{alignItems: "start", marginBottom: 24}}>
        <section className="petaar-card" style={{display: "grid", gap: 14}}>
          <div className="petaar-kicker">Booking Filters</div>
          <div className="petaar-form-grid">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by service, city, or date"
            />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="pending">pending</option>
              <option value="accepted">accepted</option>
              <option value="in_progress">in_progress</option>
              <option value="completed">completed</option>
              <option value="cancel_requested">cancel_requested</option>
              <option value="cancelled">cancelled</option>
            </select>
            <select value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)}>
              <option value="all">All Time</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
            <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value) || 8)}>
              <option value={8}>8 / page</option>
              <option value={12}>12 / page</option>
              <option value={20}>20 / page</option>
            </select>
          </div>
          <div style={{display: "flex", gap: 10, flexWrap: "wrap"}}>
            <button
              className="petaar-button petaar-button--ghost"
              onClick={() => {
                setStatusFilter("");
                setDateFilter("");
                setSearch("");
                setPeriodFilter("all");
                setCurrentPage(1);
              }}
            >
              Clear Filters
            </button>
          </div>
        </section>

        <section className="petaar-card" style={{display: "grid", gap: 12}}>
          <div className="petaar-kicker">Financial Snapshot</div>
          <div className="petaar-list-row" style={{padding: 16}}>
            <div style={{display: "flex", justifyContent: "space-between", gap: 12}}><span>Gross collected</span><strong>₹{financialSummary.grossCollected.toFixed(2)}</strong></div>
          </div>
          <div className="petaar-list-row" style={{padding: 16}}>
            <div style={{display: "flex", justifyContent: "space-between", gap: 12}}><span>Platform fee</span><strong>₹{financialSummary.platformFee.toFixed(2)}</strong></div>
          </div>
          <div className="petaar-list-row" style={{padding: 16}}>
            <div style={{display: "flex", justifyContent: "space-between", gap: 12}}><span>Pending payout</span><strong>₹{financialSummary.pendingPayout.toFixed(2)}</strong></div>
          </div>
          <div className="petaar-list-row" style={{padding: 16}}>
            <div style={{display: "flex", justifyContent: "space-between", gap: 12}}><span>Refunded amount</span><strong>₹{financialSummary.refundedAmount.toFixed(2)}</strong></div>
          </div>
        </section>
      </div>

      <section className="petaar-card">
        <div className="petaar-section-title">
          <div>
            <div className="petaar-kicker">Results</div>
            <h3 style={{fontSize: "1.35rem"}}>Active bookings board</h3>
          </div>
          <ToneBadge tone="default">
            Showing {sortedFilteredBookings.length ? ((currentPage - 1) * pageSize) + 1 : 0}-
            {Math.min(currentPage * pageSize, sortedFilteredBookings.length)} of {sortedFilteredBookings.length}
          </ToneBadge>
        </div>

        {loading ? (
          <div className="petaar-grid" style={{gap: 16}}>
            {Array.from({length: 3}).map((_, index) => (
              <div key={`booking-skeleton-${index}`} className="petaar-skeleton" style={{height: 320, borderRadius: 20}} />
            ))}
          </div>
        ) : filteredBookings.length === 0 ? (
          <EmptyState
            icon="📋"
            title="No bookings yet"
            description="Bookings assigned to this provider will appear here with chat access, consultation controls, and payout context."
          />
        ) : (
          <div className="petaar-grid" style={{gap: 18}}>
            {pagedBookings.map((b) => (
              <div key={b.id} className="petaar-list-row" style={{display: "grid", gap: 14}}>
                <div style={{display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center"}}>
                  <div>
                    <h3 style={{fontSize: "1.3rem"}}>{b.serviceTitle || b.serviceName || "Service"}</h3>
                    <div style={{marginTop: 6, color: "var(--muted)"}}>
                      Customer: {ownerLabel(b)} · {b.bookingDate || "N/A"}{b.bookingSlot ? ` · ${b.bookingSlot}` : ""}
                    </div>
                  </div>
                  <div style={{display: "flex", gap: 8, flexWrap: "wrap"}}>
                    <ToneBadge tone={bookingTone(b.status)}>{b.status || "pending"}</ToneBadge>
                    <ToneBadge tone={b.paymentStatus === "paid" ? "success" : "warning"}>{b.paymentStatus || "unknown"}</ToneBadge>
                    {isVetConsultationBooking(b) ? <ToneBadge tone="accent">{b.consultationMode || "chat"}</ToneBadge> : null}
                  </div>
                </div>

                <div className="petaar-form-grid" style={{gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))"}}>
                  <div><strong>Amount</strong><div style={{marginTop: 6, color: "var(--muted)"}}>₹{getAmount(b).toFixed(2)}</div></div>
                  <div><strong>Platform Fee</strong><div style={{marginTop: 6, color: "var(--muted)"}}>₹{getCommission(b).toFixed(2)}</div></div>
                  <div><strong>Net Payout</strong><div style={{marginTop: 6, color: "var(--muted)"}}>₹{getNet(b).toFixed(2)}</div></div>
                  <div><strong>Category</strong><div style={{marginTop: 6, color: "var(--muted)"}}>{b.category || "N/A"}</div></div>
                </div>

                {b.rescheduleReason ? (
                  <div className="petaar-notice info">
                    Rescheduled: {b.bookingDate || "N/A"} ({b.rescheduleReason})
                  </div>
                ) : null}

                {isVetConsultationBooking(b) ? (
                  <div className="petaar-card" style={{padding: 18, display: "grid", gap: 12, background: "var(--surface-alt)"}}>
                    <div style={{display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center"}}>
                      <div>
                        <div className="petaar-kicker">Consultation</div>
                        <div style={{fontWeight: 800, fontSize: "1.05rem"}}>{consultationStatusLabel(b)}</div>
                      </div>
                      {["accepted", "in_progress"].includes(String(b.status || "").toLowerCase()) ? (
                        <button
                          type="button"
                          className="petaar-button petaar-button--secondary"
                          onClick={() => openConsultationChat(b.id)}
                        >
                          Open Chat{Number(chatUnreadByBooking[b.id] || 0) > 0 ? ` (${chatUnreadByBooking[b.id]})` : ""}
                        </button>
                      ) : null}
                    </div>
                    {canJoinConsultationCall(b) ? (
                      <div style={{display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center"}}>
                        <button type="button" className="petaar-button" onClick={() => openConsultationCallWindow(b)}>
                          Join Secure {getConsultationJoinLabel(b.consultationMode)}
                        </button>
                        <small style={{color: "var(--muted)"}}>Opens private Petaar consultation room.</small>
                      </div>
                    ) : (
                      <small style={{color: "var(--muted)"}}>
                        Use Petaar chat for this consultation mode.
                      </small>
                    )}
                  </div>
                ) : null}

                {getSlaText(b) ? (
                  <div className="petaar-notice info">
                    {getSlaText(b)}
                  </div>
                ) : null}

                {b.petDetails ? (
                  <div className="petaar-card" style={{padding: 18, display: "grid", gap: 8, background: "var(--surface-alt)"}}>
                    <div className="petaar-kicker">Pet Details</div>
                    <div style={{color: "var(--muted)", lineHeight: 1.7}}>
                      <div>{b.petDetails.name || "Pet"} · {b.petDetails.species || "N/A"} · {b.petDetails.breed || "N/A"}</div>
                      <div>Age: {b.petDetails.age || "N/A"} · Weight: {b.petDetails.weight || "N/A"}</div>
                      {b.petDetails.symptoms ? <div>Symptoms: {b.petDetails.symptoms}</div> : null}
                      {(b.petDetails.allergies || b.petDetails.medications) ? (
                        <div>Allergies: {b.petDetails.allergies || "N/A"} · Medications: {b.petDetails.medications || "N/A"}</div>
                      ) : null}
                      {b.petDetails.notes ? <div>Notes: {b.petDetails.notes}</div> : null}
                    </div>
                  </div>
                ) : null}

                {isVetConsultationBooking(b) ? (
                  <div className="petaar-card" style={{padding: 18, display: "grid", gap: 12, background: "var(--surface-alt)"}}>
                    <div className="petaar-kicker">Prescription Generator</div>
                    {prescriptionsByBooking[b.id] ? (
                      <div style={{display: "grid", gap: 10}}>
                        <div style={{color: "var(--muted)"}}>
                          Prescription already sent on {formatTs(prescriptionsByBooking[b.id]?.createdAt)}
                        </div>
                        <div style={{color: "var(--muted)"}}>
                          Medicines: {prescriptionsByBooking[b.id]?.medicines || "N/A"}
                        </div>
                        <div style={{display: "flex", justifyContent: "flex-end"}}>
                          <button
                            type="button"
                            className="petaar-button petaar-button--secondary"
                            onClick={() => openBrandedPrescription({
                              prescription: prescriptionsByBooking[b.id],
                              booking: b,
                            })}
                          >
                            Open / Print Prescription
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{display: "grid", gap: 10}}>
                        <input
                          placeholder="Diagnosis"
                          value={prescriptionDrafts[b.id]?.diagnosis || ""}
                          onChange={(e) => setPrescriptionDrafts((prev) => ({
                            ...prev,
                            [b.id]: {
                              ...(prev[b.id] || {}),
                              diagnosis: e.target.value,
                            },
                          }))}
                        />
                        <textarea
                          placeholder="Medicines / dosage plan"
                          value={prescriptionDrafts[b.id]?.medicines || ""}
                          onChange={(e) => setPrescriptionDrafts((prev) => ({
                            ...prev,
                            [b.id]: {
                              ...(prev[b.id] || {}),
                              medicines: e.target.value,
                            },
                          }))}
                          style={{minHeight: 70}}
                        />
                        <textarea
                          placeholder="Advice / precautions"
                          value={prescriptionDrafts[b.id]?.advice || ""}
                          onChange={(e) => setPrescriptionDrafts((prev) => ({
                            ...prev,
                            [b.id]: {
                              ...(prev[b.id] || {}),
                              advice: e.target.value,
                            },
                          }))}
                          style={{minHeight: 60}}
                        />
                        <input
                          type="date"
                          value={prescriptionDrafts[b.id]?.followUpDate || ""}
                          onChange={(e) => setPrescriptionDrafts((prev) => ({
                            ...prev,
                            [b.id]: {
                              ...(prev[b.id] || {}),
                              followUpDate: e.target.value,
                            },
                          }))}
                        />
                        <div style={{display: "flex", justifyContent: "flex-end"}}>
                          <button
                            className="petaar-button"
                            onClick={() => sendPrescription(b)}
                            disabled={sendingPrescriptionFor === b.id}
                          >
                            {sendingPrescriptionFor === b.id ? "Sending..." : "Send Prescription"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}

                {b.cancellationReason ? (
                  <div className="petaar-notice error">
                    Cancellation Reason: {b.cancellationReason}
                  </div>
                ) : null}

                <div style={{display: "flex", gap: 10, flexWrap: "wrap"}}>
                  {(b.status === "pending" || b.status === "pending_assignment") ? (
                    <>
                      <button
                        className="petaar-button"
                        onClick={() => updateStatus(b.id, "accepted")}
                        disabled={updatingId === b.id}
                      >
                        {isVetConsultationBooking(b) ? "Accept Consultation" : "Accept"}
                      </button>
                      <button
                        className="petaar-button petaar-button--ghost"
                        onClick={() => updateStatus(b.id, "rejected")}
                        disabled={updatingId === b.id}
                      >
                        Reject
                      </button>
                    </>
                  ) : null}
                  {b.status === "accepted" ? (
                    <>
                      <button
                        className="petaar-button"
                        onClick={() => updateStatus(b.id, "in_progress")}
                        disabled={updatingId === b.id}
                      >
                        {isVetConsultationBooking(b) ? "Start Consultation" : "Start Service"}
                      </button>
                      <button
                        className="petaar-button petaar-button--secondary"
                        onClick={() => updateStatus(b.id, "completed")}
                        disabled={updatingId === b.id}
                      >
                        {isVetConsultationBooking(b) ? "Complete Consultation" : "Mark Completed"}
                      </button>
                    </>
                  ) : null}
                  {b.status === "in_progress" ? (
                    <button
                      className="petaar-button"
                      onClick={() => updateStatus(b.id, "completed")}
                      disabled={updatingId === b.id}
                    >
                      {isVetConsultationBooking(b) ? "Complete Consultation" : "Mark Completed"}
                    </button>
                  ) : null}
                  {b.status === "cancel_requested" ? (
                    <button
                      className="petaar-button petaar-button--ghost"
                      onClick={() => updateStatus(b.id, "cancelled")}
                      disabled={updatingId === b.id}
                    >
                      Approve Cancellation
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}

        {sortedFilteredBookings.length > 0 && totalPages > 1 ? (
          <div style={{display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 18}}>
            <button className="petaar-button petaar-button--ghost" onClick={() => setCurrentPage(1)} disabled={currentPage <= 1}>First</button>
            <button className="petaar-button petaar-button--ghost" onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))} disabled={currentPage <= 1}>
              Previous
            </button>
            <ToneBadge tone="default">Page {currentPage} of {totalPages}</ToneBadge>
            <button
              className="petaar-button petaar-button--ghost"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages}
            >
              Next
            </button>
            <button className="petaar-button petaar-button--ghost" onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages}>Last</button>
          </div>
        ) : null}
      </section>
    </div>
  );
}
